/* eslint-disable eqeqeq */
/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
import React from 'react';
import { find, cloneDeep, first, isNil } from 'lodash';
import moment from 'moment-timezone';
import * as queryString from 'query-string';

import { paymentStatusTypeIds, registrationGroupStatusTypeIds, registrationStatusTypeIds, utrTypeIds, utrTypes, acceptanceBucketTypeIds, waitlistBucketTypeIds } from './eventConstants';
import { RatingStatuses, RatingDisplayStates, HISTORIC_RATING_MARKER, gameTypes, ratingTypes, CLASS_YEAR_OPTIONS, CLUB_PROFILE_VIEW_TYPES, HS_CLASS_YEARS, getRedshirtClassYearOptions } from './constants';
import { ComparatorTypes } from './comparator';
import { titleCase, countWords } from './formatting';
import clubs from '../../constants/clubs';

export { countWords };

const _ = { find, cloneDeep, first, isNil };

export const playerTypes = {
    WINNER: 'winner',
    LOSER: 'loser',
};

export const eventDirectorRoles = {
    HEAD_ORGANIZER: 2,
    ORGANIZER: 1,
};

export function removeQueryParam(param, props) {
    const params = queryString.parse(props.location.search);
    delete params[param];
    props.history.push({
        pathname: props.location.pathname,
        search: queryString.stringify(params),
    });
}

export function getFlagUrl(countryCode) {
    if (countryCode) {
        return `/img/flags/${countryCode.toLowerCase()}.svg`;
    }
    return '';
}

export function getPlayerName(firstName, lastName, shortenFirstName) {
    const titleCasedFirstName = titleCase(firstName);
    const titleCasedLastName = titleCase(lastName);

    let firstCharacter = '';

    if (titleCasedFirstName?.length > 0) {
        if (shortenFirstName) {
            firstCharacter = `${titleCasedFirstName.charAt(0)}.`;
        } else {
            firstCharacter = titleCasedFirstName;
        }
    }

    if (titleCasedLastName?.length > 0) {
        return `${firstCharacter} ${titleCasedLastName}`;
    }
    return firstCharacter;
}

export function formatPlayerShots(dominantHand, backHand) {
    let shots = '';

    if (dominantHand !== null) {
        shots += dominantHand;
    }
    if (backHand !== null) {
        if (shots.length !== 0) {
            shots += ', ';
        }
        shots += `${backHand} Backhand`;
    }

    return shots.length > 0 ? shots : '--';
}

export function getThirdPartyRanking(thirdPartyRankings, rankingSource, isPro) {
    // ranks come ordered by date from the api so just grab the first one
    const ranking = _.first(thirdPartyRankings, r => r.source === rankingSource);
    return ranking && isPro ? `#${ranking.rank}` : `Former ${rankingSource} Ranked Player`;
}

export function getExtendedRatingAndRank(extendedRatingProfile, type, showOnly) {
    const ratingType = type || 'threeMonth';
    const rankingType = `${ratingType}Rank`;

    let rating = '';
    let ranking = '';
    if (extendedRatingProfile && extendedRatingProfile.ratings !== null && extendedRatingProfile.ratings[ratingType] !== null) {
        rating = Number(extendedRatingProfile.ratings[ratingType]).toFixed(2);
    }
    if (extendedRatingProfile && extendedRatingProfile.rankings !== null && extendedRatingProfile.rankings[rankingType] !== null) {
        ranking = `Rank ${extendedRatingProfile.rankings[rankingType]}`;
    }
    switch (showOnly) {
        case 'rating':
            return rating;
        case 'rank':
            return ranking;
        default:
            return `${rating}${(rating.length > 0 && ranking.length > 0) ? ' (' : ''}${ranking}${(rating.length > 0 && ranking.length > 0) ? ')' : ''}`;
    }
}

export function wonGame(players, playerId) {
    if (players?.winner1 == playerId || players?.winner1?.id == playerId) {
        return true;
    }
    if (players?.winner2 != undefined && (players?.winner2 == playerId || players?.winner2?.id == playerId)) {
        return true;
    }
    return false;
}

export const wonSet = (score, playerType) => {
    switch (playerType) {
        case playerTypes.WINNER:
            return score.winner > score.loser;
        case playerTypes.LOSER:
            return score.loser > score.winner;
        default:
            return false;
    }
};

export const tieBreakScore = (score, playerType) => {
    const superTieBreaker = score.winner === 1 && score.loser === 0;
    const won = wonSet(score, playerType);
    if (!won) {
        return score.tiebreak;
    }
    if (superTieBreaker) {
        return score.tiebreak < 9 ? 10 : score.tiebreak + 2;
    }
    return score.tiebreak < 6 ? 7 : score.tiebreak + 2;
};

export const truncateString = (string, noOfWords) => {
    if (!noOfWords) {
        return string;
    }
    string = string.trim().split(' ');
    return string.splice(0, noOfWords).join(' ');
};

export const playerSortOptions = [
    {
        label: `Singles ${ratingTypes.tennis} & Status`,
        value: 1,
        sortParams: [
            {
                sortVariable: 'ratingStatusSingles',
                sortAsc: false,
            },
            {
                sortVariable: 'singlesUtr',
                sortAsc: false,
            },
        ],
    },
    {
        label: `Singles ${ratingTypes.tennis}`,
        value: 2,
        sortParams: [
            {
                sortVariable: 'singlesUtr',
                sortAsc: false,
            },
        ],
    },
    {
        label: `Doubles ${ratingTypes.tennis} & Status`,
        value: 3,
        sortParams: [
            {
                sortVariable: 'ratingStatusDoubles',
                sortAsc: false,
            },
            {
                sortVariable: 'doublesUtr',
                sortAsc: false,
            },
        ],
    },
    {
        label: `Doubles ${ratingTypes.tennis}`,
        value: 4,
        sortParams: [
            {
                sortVariable: 'doublesUtr',
                sortAsc: false,
            },
        ],
    },
    {
        label: 'First Name',
        value: 5,
        sortParams: [
            {
                sortVariable: 'firstName',
                sortAsc: true,
            },
        ],
    },
    {
        label: 'Last Name',
        value: 6,
        sortParams: [
            {
                sortVariable: 'lastName',
                sortAsc: true,
            },
        ],
    },
];

export const schoolSortOptions = [
    {
        label: 'Power 6',
        value: 1,
        sortParams: [
            {
                sortVariable: 'power6',
                sortAsc: false,
            },
        ],
    },
    {
        label: 'Name',
        value: 2,
        sortParams: [
            {
                sortVariable: 'displayName',
                sortAsc: true,
            },
        ],
    },
];

export const ratingStatuses = [
    {
        label: 'Rated',
        value: 3,
    },
    {
        label: 'Projected',
        value: 2,
    },
    {
        label: 'Inactive',
        value: 1,
    },
    {
        label: 'Unrated',
        value: 0,
    },
];

const isActiveRating = (value, status) => {
    return value && value > 0 && !['Inactive', 'Unrated'].includes(status);
};

const isSanctioned = (utrType) => {
    return utrType === utrTypes.SANCTIONED || utrType === utrTypeIds.SANCTIONED;
};

const applyUnratedFallback = (obj, ratingValue, ratingStatus, statusKey, valueKey) => {
    if (ratingValue && ratingValue > 0 && ratingStatus === 'Inactive') {
        return; // preserve inactive rating
    }
    obj[statusKey] = 'Unrated';
    obj[valueKey] = 0;
};

const resolveWithBothParams = (obj, ratingType, utrType) => {
    if (ratingType === gameTypes.SINGLES) {
        obj.ratingChoice = isSanctioned(utrType) ? 'VerifiedSinglesUTR' : 'SinglesUTR';
    } else {
        obj.ratingChoice = isSanctioned(utrType) ? 'VerifiedDoublesUTR' : 'DoublesUTR';
    }
};

const resolveWithRatingType = (obj, player, ratingType) => {
    if (ratingType === gameTypes.SINGLES) {
        obj.ratingChoice = isActiveRating(player.singlesUtr, player.ratingStatusSingles) ? 'VerifiedSinglesUTR' : 'SinglesUTR';
    } else {
        obj.ratingChoice = isActiveRating(player.doublesUtr, player.ratingStatusDoubles) ? 'VerifiedDoublesUTR' : 'DoublesUTR';
    }
};

const resolveWithSanctionedType = (obj, player) => {
    if (isActiveRating(player.singlesUtr, player.ratingStatusSingles)) {
        obj.ratingChoice = 'VerifiedSinglesUTR';
        return;
    }
    if (isActiveRating(player.doublesUtr, player.ratingStatusDoubles)) {
        obj.ratingChoice = 'VerifiedDoublesUTR';
        return;
    }
    obj.ratingChoice = 'VerifiedSinglesUTR';
    applyUnratedFallback(obj, player.singlesUtr, player.ratingStatusSingles, 'ratingStatusSingles', 'singlesUtr');
};

const resolveWithMyUtrType = (obj, player) => {
    if (isActiveRating(player.myUtrSingles, player.myUtrStatusSingles)) {
        obj.ratingChoice = 'SinglesUTR';
        return;
    }
    if (isActiveRating(player.myUtrDoubles, player.myUtrStatusDoubles)) {
        obj.ratingChoice = 'DoublesUTR';
        return;
    }
    obj.ratingChoice = 'SinglesUTR';
    applyUnratedFallback(obj, player.myUtrSingles, player.myUtrStatusSingles, 'myUtrStatusSingles', 'myUtrSingles');
};

const resolveDefaultChoice = (obj, player) => {
    if (player.ratingChoice) {
        // if the player is unclaimed, use the doubles ranking if it exists and is higher
        if (!player.claimed && !player.myUtrSingles > 0 && player.myUtrDoubles > 0) {
            obj.ratingChoice = 'VerifiedDoublesUTR';
        }
        return;
    }
    if (isActiveRating(player.singlesUtr, player.ratingStatusSingles)) {
        obj.ratingChoice = 'VerifiedSinglesUTR';
        return;
    }
    if (isActiveRating(player.doublesUtr, player.ratingStatusDoubles)) {
        obj.ratingChoice = 'VerifiedDoublesUTR';
        return;
    }
    if (isActiveRating(player.myUtrSingles, player.myUtrStatusSingles)) {
        obj.ratingChoice = 'SinglesUTR';
        return;
    }
    if (isActiveRating(player.myUtrDoubles, player.myUtrStatusDoubles)) {
        obj.ratingChoice = 'DoublesUTR';
        return;
    }
    if (player.pbrRatingDisplay != null || player.utrRange?.pbrRange != null) {
        obj.ratingChoice = 'ProgressionBallRating';
        return;
    }
    obj.ratingChoice = 'SinglesUTR';
    applyUnratedFallback(obj, player.myUtrSingles, player.myUtrStatusSingles, 'myUtrStatusSingles', 'myUtrSingles');
};

// supports v2 typeIds
export const setRatingChoice = ({ player, ratingType, utrType }) => {
    const { ratingChoice, myUtrSingles, myUtrSinglesDisplay, myUtrStatusSingles, myUtrProgressSingles, myUtrDoubles, myUtrDoublesDisplay, myUtrStatusDoubles, myUtrProgressDoubles, singlesUtr, singlesUtrDisplay, ratingStatusSingles, ratingProgressSingles, doublesUtr, doublesUtrDisplay, ratingStatusDoubles, ratingProgressDoubles, utrRange, pbrRatingDisplay, showTennisRating = true, showPickleballRating = false } = player || {};

    const historicRatings = {};
    if (player?.historicRatings !== null) {
        historicRatings.historicSinglesRating = player?.historicRatings?.historicSinglesRating;
        historicRatings.historicSinglesRatingDate = player?.historicRatings?.historicSinglesRatingDate;
        historicRatings.historicSinglesRatingReliability = player?.historicRatings?.historicSinglesRatingReliability;
        historicRatings.historicSinglesRatingDisplay = player?.historicRatings?.historicSinglesRatingDisplay;
        historicRatings.historicDoublesRating = player?.historicRatings?.historicDoublesRating;
        historicRatings.historicDoublesRatingDate = player?.historicRatings?.historicDoublesRatingDate;
        historicRatings.historicDoublesRatingReliability = player?.historicRatings?.historicDoublesRatingReliability;
        historicRatings.historicDoublesRatingDisplay = player?.historicRatings?.historicDoublesRatingDisplay;
    }

    const newRatingObject = player ? { ratingChoice, myUtrSingles, myUtrSinglesDisplay, myUtrStatusSingles, myUtrProgressSingles, myUtrDoubles, myUtrDoublesDisplay, myUtrStatusDoubles, myUtrProgressDoubles, singlesUtr, singlesUtrDisplay, ratingStatusSingles, ratingProgressSingles, doublesUtr, doublesUtrDisplay, ratingStatusDoubles, ratingProgressDoubles, utrRange, historicRatings, pbrRatingDisplay, showTennisRating, showPickleballRating } : {};

    if (ratingType && utrType) {
        resolveWithBothParams(newRatingObject, ratingType, utrType);
    } else if (ratingType) {
        resolveWithRatingType(newRatingObject, player, ratingType);
    } else if (utrType) {
        if (isSanctioned(utrType)) {
            resolveWithSanctionedType(newRatingObject, player);
        } else {
            resolveWithMyUtrType(newRatingObject, player);
        }
    } else {
        resolveDefaultChoice(newRatingObject, player);
    }

    return newRatingObject;
};

export const getRatingStatus = (ratingStatus, ratingValue) => {
    const ratedStrings = ['Projected', 'Inactive', 'Rated'];
    if (ratingValue && ratingValue > 0 && ratedStrings.includes(ratingStatus)) {
        return ratingStatus;
    }
    /*
        If rating is 0 even though the rating status is available make it unrated
        and rating status is unrated consider it as unrated and then check for projected status if rating exists
    */
    if (ratingValue && ratingValue > 0) {
        // If unrated and there is a rating condider the rating to be projected
        return 'Projected';
    }
    return 'Unrated';
};

/*
Progress should be made 0 in case of status Unrated and a rating value exists
 */
export const getRatingProgress = (ratingStatus, ratingValue, ratingProgress) => {
    const ratedStrings = ['Projected', 'Inactive', 'Rated'];
    if (ratingValue && ratingValue > 0 && ratedStrings.includes(ratingStatus)) {
        return ratingProgress;
    }
    if (ratingValue && ratingValue > 0) {
        return 0;
    }
    return ratingProgress;
};

export const ratingStatusPopUpData = {
    rated: {
        title: 'Established Rating',
        description: 'This player has played enough matches for their rating to be reliable. It usually takes 6-10 matches to get a reliable rating, depending on the reliability of your opponent and how close the score was.',
    },
    unrated: {
        title: 'Unrated',
        description: 'This player has not played any matches or had a rating in the past year.',
    },
    inactive: {
        title: 'Inactive Rating',
        description: `This player has not played any matches in the past 12 months. Their rating is the last time they had a rating on ${ratingTypes.tennis}.`,
    },
    projected: {
        title: 'Projected Rating',
        description: 'This player hasn’t played enough matches in the last 12 months for their rating to be reliable.  It usually takes 6-10 matches to get a reliable rating, depending on the reliability of your opponent and how close the score was.',
    },
};

const getSortValue = (status, verified) => {
    if (status === 'Rated') {
        return verified ? 5 : 3;
    }
    return verified ? 4 : 2;
};

const RATING_CHOICE_FIELDS = {
    SinglesUTR: { raw: 'myUtrSingles', display: 'myUtrSinglesDisplay', status: 'myUtrStatusSingles', progress: 'myUtrProgressSingles', matchType: 'singles', verified: false },
    DoublesUTR: { raw: 'myUtrDoubles', display: 'myUtrDoublesDisplay', status: 'myUtrStatusDoubles', progress: 'myUtrProgressDoubles', matchType: 'doubles', verified: false },
    VerifiedDoublesUTR: { raw: 'doublesUtr', display: 'doublesUtrDisplay', status: 'ratingStatusDoubles', progress: 'ratingProgressDoubles', matchType: 'doubles', verified: true },
    VerifiedSinglesUTR: { raw: 'singlesUtr', display: 'singlesUtrDisplay', status: 'ratingStatusSingles', progress: 'ratingProgressSingles', matchType: 'singles', verified: true },
};

const SINGLES_HISTORIC_CHOICES = ['SinglesUTR', 'VerifiedSinglesUTR', 'ProgressionBallRating'];
const DOUBLES_HISTORIC_CHOICES = ['DoublesUTR', 'VerifiedDoublesUTR', 'ProgressionBallRating'];

// A rating is "fully reliable" at exactly 100%; anything below it is still projecting. Named so the
// two display states do not hinge on a bare literal.
const FULLY_RELIABLE_PROGRESS = 100;

const applyHistoricRating = (object, historicRating, historicDisplay, historicDate, doNotMask, matchType, historicVerified) => {
    object.value = doNotMask ? `${historicRating}${HISTORIC_RATING_MARKER}` : `${historicDisplay}${HISTORIC_RATING_MARKER}`;
    object.title = `${matchType} UTR Rating from ${moment(historicDate).format('MMMM YYYY')}`;
    object.sortRating = historicDisplay;
    object.verified = false;
    object.displayState = RatingDisplayStates.HISTORIC;
    // Published so a sub-label can date the rating without re-deciding which side's historic rating
    // won here - that second decision is what TMS-2226 removes.
    object.historicDate = historicDate;
    // `verified` above stays false, unchanged, for the consumers that don't opt into the historic
    // verified-checkmark (TMS-2226) - this is published separately so `Utr`'s `historicVerifiedIcon`
    // opt-in has something to key off without touching that meaning.
    object.historicVerified = historicVerified;
};

const resolveUtrRangeDisplay = (object, utrRange, doNotMask) => {
    if (!utrRange.minUtr && utrRange.lastReliableRating) {
        object.value = `${doNotMask ? utrRange.lastReliableRating : utrRange.lastReliableRatingDisplay}`;
        object.title = 'Last known Verified Singles UTR';
        object.sortRating = utrRange.lastReliableRating.toFixed(2);
        return;
    }
    if (utrRange.minUtr && utrRange.maxUtr) {
        object.value = `${doNotMask ? utrRange.minUtr.toFixed(2) : utrRange.minUtrDisplay}-${doNotMask ? utrRange.maxUtr.toFixed(2) : utrRange.maxUtrDisplay}`;
        object.title = 'Estimated UTR Rating';
        object.sortRating = ((utrRange.minUtr + utrRange.maxUtr) / 2).toFixed(2);
        object.displayState = RatingDisplayStates.ESTIMATED;
        return;
    }
    object.value = 'UR';
    object.title = 'Unrated';
    object.displayState = RatingDisplayStates.UNRATED;
};

const resolveUnratedDisplay = (object, rating, isDoubles, doNotMask) => {
    const hr = rating.historicRatings;
    if (!_.isNil(hr?.historicSinglesRating) && SINGLES_HISTORIC_CHOICES.includes(rating.ratingChoice)) {
        applyHistoricRating(object, hr.historicSinglesRating, hr.historicSinglesRatingDisplay, hr.historicSinglesRatingDate, doNotMask, 'Singles', RATING_CHOICE_FIELDS[rating.ratingChoice]?.verified ?? false);
        return;
    }

    if ((rating.pbrRatingDisplay || rating.utrRange?.pbrRange) && !isDoubles) {
        object.showPBRRating = true;
        return;
    }

    if (!_.isNil(hr?.historicDoublesRating) && DOUBLES_HISTORIC_CHOICES.includes(rating.ratingChoice)) {
        applyHistoricRating(object, hr.historicDoublesRating, hr.historicDoublesRatingDisplay, hr.historicDoublesRatingDate, doNotMask, 'Doubles', RATING_CHOICE_FIELDS[rating.ratingChoice]?.verified ?? false);
        return;
    }

    if (rating.utrRange) {
        resolveUtrRangeDisplay(object, rating.utrRange, doNotMask);
        return;
    }

    object.value = 'UR';
    object.title = 'Unrated';
    object.displayState = RatingDisplayStates.UNRATED;
};

const finalizeRatingObject = (object) => {
    if (object.showPBRRating) {
        object.value = 'UR';
        object.title = ratingTypes.colorBall;
        object.status = 'Unrated';
    }

    object.ratingNumber = (object.value !== 'UR') ? `${parseInt(object.value)}` : object.value;

    const ratingArray = object.value.split('.');
    object.ratingDecimal = (ratingArray.length > 1) ? ratingArray[1] : '';

    if (object.status) {
        object.infoTitle = ratingStatusPopUpData[object.status.toLowerCase()].title;
        object.infoDescription = ratingStatusPopUpData[object.status.toLowerCase()].description;
    }
};

// A live rating splits on reliability, not on status: TMS-2226 separates a rating that has reached
// 100% from one still climbing, and `Projected` is what the platform calls the latter. Extracted from
// `getRatingDetails` rather than inlined there to keep that function inside its complexity budget.
const resolveLiveRatingDisplay = (object, utrValue) => {
    object.value = `${utrValue}`;
    // `object.progress` is the raw `ratingProgress*` field straight off the API, so a string "100"
    // or a 99.99 float would fall through to PROJECTED and mislabel ~98% of the tab.
    object.displayState = Number(object.progress) >= FULLY_RELIABLE_PROGRESS
        ? RatingDisplayStates.FULLY_RELIABLE
        : RatingDisplayStates.PROJECTED;
};

export const getRatingDetails = (rating, userIsPowered, showDecimals, doNotMask = false, showProjectedViewStatus = false) => {
    const object = {
        verified: false,
        showPBRRating: false,
        utrRange: rating.utrRange,
        pbrRating: rating.pbrRatingDisplay || rating.utrRange?.pbrRange,
        pkbRating: getPickleBallRating(rating),
        showPickleballRating: rating.showPickleballRating,
        showTennisRating: rating.showTennisRating,
    };
    const isDoubles = rating.ratingChoice === 'DoublesUTR' || rating.ratingChoice === 'VerifiedDoublesUTR';

    // Handle ProgressionBallRating as special case
    let utrValue;
    if (rating.ratingChoice === 'ProgressionBallRating') {
        if (object.pbrRating) {
            object.showPBRRating = true;
        }
        object.status = 'Unrated';
    } else {
        const fields = RATING_CHOICE_FIELDS[rating.ratingChoice] || RATING_CHOICE_FIELDS.VerifiedSinglesUTR;
        utrValue = doNotMask ? rating[fields.raw] : rating[fields.display];
        object.status = getRatingStatus(rating[fields.status], rating[fields.raw]);
        object.verified = fields.verified;
        object.matchType = fields.matchType;
        object.sortValue = getSortValue(object.status, fields.verified);
        object.progress = getRatingProgress(rating[fields.status], rating[fields.raw], rating[fields.progress]);
    }

    let parseValue = utrValue;
    if (utrValue && typeof utrValue === 'string') {
        parseValue = parseFloat(utrValue);
    }

    object.utrType = (object.status === 'Projected') ? 'Projected' : 'UTR';
    object.actualValue = utrValue;
    object.sortRating = utrValue;

    if (object.status === 'Projected') {
        object.title = 'Projected';
        if (showProjectedViewStatus) {
            utrValue = `${utrValue}`;
        }
    }

    // `Inactive` is grouped with `Unrated` on purpose, not left unhandled: an inactive player reads
    // exactly like an unrated one, so it takes the same fallback chain - historic rating first, then
    // an estimated range, and only `UR` when there is neither (Satya, 2026-08-20, closing TMS-2226's
    // open product question). This is why an Inactive player's own live rating is intentionally not
    // shown here even though `applyUnratedFallback` preserves it: the fallback chain, not the live
    // value, is what this column renders.
    if (!parseValue || parseValue === 0 || object.status === 'Unrated' || object.status === 'Inactive') {
        resolveUnratedDisplay(object, rating, isDoubles, doNotMask);
        object.sortValue = 1;
    } else {
        resolveLiveRatingDisplay(object, utrValue);
    }

    finalizeRatingObject(object);
    return object;
};

const formatUtrRating = (utr, ratingStatus, myUtr, subscriptionType) => {
    const utrRatingStatuses = ['Rated', 'Projected'];
    if (utrRatingStatuses.indexOf(ratingStatus) !== -1 && parseInt(utr) !== 0) {
        return subscriptionType === 'Free' ? `${utr}` : `${utr?.toFixed(2)}`;
    }
    if (myUtr > 0) {
        return `${myUtr.toFixed(1)}`;
    }
    return '';
};

export const getUtrRatingDisplay = (profileData, subscriptionType, type) => {
    if (type === 'singles') {
        return formatUtrRating(profileData.singlesUtr, profileData.ratingStatusSingles, profileData.myUtrSingles, subscriptionType);
    }
    if (type === 'doubles') {
        return formatUtrRating(profileData.doublesUtr, profileData.ratingStatusDoubles, profileData.myUtrDoubles, subscriptionType);
    }
    return '';
};

export const getMatchTypeIndicator = (rating) => {
    return (
        <React.Fragment>
            <span className="match-type-indicator" />
            {rating.matchType === 'doubles' && (
                <span className="match-type-indicator" />
            )}
        </React.Fragment>
    );
};

export const getPickleBallRating = (ratingObject, ratingType = null) => {
    const { singlesPkbRatingDisplay, doublesPkbRatingDisplay, utrRange } = ratingObject || {};
    const object = {
        title: 'Pickleball Rating',
        type: ratingTypes.pickleball,
        value: utrRange?.pkbRatingDisplay || 'UR',
        status: utrRange?.pkbRatingDisplay ? RatingStatuses.ESTIMATED : RatingStatuses.UNRATED,
        reliability: utrRange?.pkbRatingDisplay ? RatingStatuses.ESTIMATED : RatingStatuses.UNRATED,
        sortRating: utrRange?.pkbRating || 0,
        isRated: !!utrRange?.pkbRatingDisplay,
    };

    if (ratingType === gameTypes.SINGLES) {
        if (singlesPkbRatingDisplay) {
            object.value = singlesPkbRatingDisplay.pkbRating;
            object.sortRating = singlesPkbRatingDisplay.pkbRating || 0;
            object.reliability = singlesPkbRatingDisplay.reliability;
            object.verified = singlesPkbRatingDisplay.isVerified;
            object.status = singlesPkbRatingDisplay.reliability;
            object.isRated = true;
        }
    } else if (ratingType === gameTypes.DOUBLES) {
        if (doublesPkbRatingDisplay) {
            object.value = doublesPkbRatingDisplay.pkbRating;
            object.sortRating = doublesPkbRatingDisplay.pkbRating || 0;
            object.reliability = doublesPkbRatingDisplay.reliability;
            object.verified = doublesPkbRatingDisplay.isVerified;
            object.status = doublesPkbRatingDisplay.reliability;
            object.isRated = true;
        }
    } else if (doublesPkbRatingDisplay) {
        object.value = doublesPkbRatingDisplay.pkbRating;
        object.sortRating = doublesPkbRatingDisplay.pkbRating || 0;
        object.reliability = doublesPkbRatingDisplay.reliability;
        object.verified = doublesPkbRatingDisplay.isVerified;
        object.status = doublesPkbRatingDisplay.reliability;
        object.isRated = true;
    } else if (singlesPkbRatingDisplay) {
        object.value = singlesPkbRatingDisplay.pkbRating;
        object.sortRating = singlesPkbRatingDisplay.pkbRating || 0;
        object.reliability = singlesPkbRatingDisplay.reliability;
        object.verified = singlesPkbRatingDisplay.isVerified;
        object.status = singlesPkbRatingDisplay.reliability;
        object.isRated = true;
    }

    return object;
};

export const savedListTypes = {
    players: {
        displayName: 'Players',
        key: 'players',
    },
    schools: {
        displayName: 'Schools',
        key: 'schools',
    },
};

export function newGuid() {
    /* eslint no-bitwise: [2, { allow: ["^","&", ">>"] }] */
    // eslint-disable-next-line no-mixed-operators
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
}

export function dictFromArray(arr) {
    const obj = {};
    arr.forEach(el => {
        obj[el.id] = el;
    });
    return obj;
}

export function dictFromArrayByProp(arr, prop) {
    const obj = {};
    arr.forEach(el => {
        obj[el[prop]] = el;
    });
    return obj;
}

export const rankListTypeIds = {
    FEMALE_WORLD: 1,
    MALE_WORLD: 2,
    FEMALE_PRO: 41,
    MALE_PRO: 46,
};

export function arraysEqual(_arr1, _arr2) {
    if (!Array.isArray(_arr1) || !Array.isArray(_arr2) || _arr1.length !== _arr2.length) { return false; }

    const arr1 = _arr1.concat().sort((a, b) => b - a);
    const arr2 = _arr2.concat().sort((a, b) => b - a);

    for (let i = 0; i < arr1.length; i += 1) {
        if (arr1[i] !== arr2[i]) { return false; }
    }

    return true;
}

export function findDrawById(draws, selectedDrawId) {
    let draw;

    draws.forEach(drawObj => {
        if (drawObj.id === selectedDrawId) {
            draw = drawObj;
        } else if (drawObj.childDraws?.length) {
            drawObj.childDraws.forEach(childDraw => {
                if (childDraw.id === selectedDrawId) {
                    draw = childDraw;
                }
            });
        }
    });
    return draw;
}

export const getPlayerGradYearOptions = (onlyYearLabel) => {
    const gradYearOptions = [];
    const currentYear = moment().year();
    const isAfterJuly = moment().month() > 6;
    const start = isAfterJuly ? currentYear : currentYear - 1;
    const end = currentYear + 7;
    for (let year = start; year <= end; year += 1) {
        const yearsAway = year - currentYear;
        let classNameLabel = '';
        if (isAfterJuly) {
            switch (yearsAway) {
                case 0:
                    classNameLabel = 'Graduated';
                    break;
                case 1:
                    classNameLabel = 'Senior';
                    break;
                case 2:
                    classNameLabel = 'Junior';
                    break;
                case 3:
                    classNameLabel = 'Sophomore';
                    break;
                case 4:
                    classNameLabel = 'Freshman';
                    break;
                default:
                    classNameLabel = 'Pre-Freshman';
                    break;
            }
        } else {
            switch (yearsAway) {
                case -1:
                    classNameLabel = 'Graduated';
                    break;
                case 0:
                    classNameLabel = 'Senior';
                    break;
                case 1:
                    classNameLabel = 'Junior';
                    break;
                case 2:
                    classNameLabel = 'Sophomore';
                    break;
                case 3:
                    classNameLabel = 'Freshman';
                    break;
                default:
                    classNameLabel = 'Pre-Freshman';
                    break;
            }
        }
        gradYearOptions.push({ id: year, label: onlyYearLabel ? `${year}` : `${year} - ${classNameLabel}` });
    }
    return gradYearOptions;
};

export const getClassYearOptions = () => {
    return CLASS_YEAR_OPTIONS;
};

export const getRosterGradYearOptions = (isNewRosterModel, isCollege, showAddPlayer, currentRedshirt) => {
    if (!isNewRosterModel) {
        return getPlayerGradYearOptions(true);
    }
    if (!isCollege) {
        return getClassYearOptions();
    }
    const shouldShowAllOptions = showAddPlayer || !currentRedshirt;
    return shouldShowAllOptions ? getClassYearOptions() : getRedshirtClassYearOptions();
};

// Grad year of the current senior class. Rolls over on clubs.hsGraduationRollover; UTC to match the API.
export const getSeniorGradYear = (now = moment.utc()) => {
    const { month, day } = clubs.hsGraduationRollover;
    const rolloverPassed = now.month() + 1 > month || (now.month() + 1 === month && now.date() >= day);
    return rolloverPassed ? now.year() + 1 : now.year();
};

// HS dropdown options, e.g. "Senior (2027)". Value is the grad year (what the roster saves).
export const getHighSchoolClassYearOptions = () => {
    const seniorGradYear = getSeniorGradYear();
    return HS_CLASS_YEARS.map(({ label, gradYearOffset }) => {
        const gradYear = seniorGradYear + gradYearOffset;
        return { id: gradYear, label: `${label} (${gradYear})` };
    });
};

export const getNCSAPlayerGradYearOptions = () => {
    const gradYearOptions = [];
    const currentYear = moment().year();
    const isAfterJuly = moment().month() > 6;
    const start = isAfterJuly ? currentYear : currentYear - 1;
    const end = currentYear + 4;
    for (let year = start; year <= end; year += 1) {
        gradYearOptions.push({ id: year, label: `${year}` });
    }
    return gradYearOptions;
};

export const getPlayerGradClassNameAbbreviation = (gradClassName) => {
    switch (gradClassName) {
        case 'Freshman':
            return 'Fr';
        case 'Sophomore':
            return 'So';
        case 'Junior':
            return 'Jr';
        case 'Senior':
            return 'Sr';
        default:
            return 'Pre-Fr';
    }
};

export const getPlayerGenderOptions = () => {
    return [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
    ];
};

export const cleanObject = (obj, removeEmptyObjects) => {
    if (!obj) {
        return obj;
    }

    const isPlainObject = (val) => typeof val === 'object' && val !== null && Object.getPrototypeOf(val) === Object.prototype;

    return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => {
            if (value == null || value === '') {
                return false;
            }

            if (removeEmptyObjects && isPlainObject(value) && Object.keys(value).length === 0) {
                return false;
            }

            return true;
        }),
    );
};

// sorts object by prop name - use: array.sort('name') or array.sort('-name')
export const sortObjectsByProp = (property) => {
    let sortOrder = 1;
    // for descending order prefix prop name with '-'
    if (property[0] === '-') {
        sortOrder = -1;
        property = property.substr(1);
    }
    return (a, b) => {
        // works with strings and numbers
        let result = 0;
        if (a[property] < b[property]) {
            result = -1;
        } else if (a[property] > b[property]) {
            result = 1;
        }
        return result * sortOrder;
    };
};

export const getAgeByBirthDate = (birthDate) => {
    if (birthDate) {
        return moment().diff(birthDate, 'years');
    }
    return null;
};

export const isEmptyObj = (obj) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
        // eslint-disable-next-line no-prototype-builtins
        if (obj.hasOwnProperty(key)) {
            return false;
        }
    }
    return true;
};

// Merges a live-score patch into a match object (handles liveMatch.periodScores preservation).
export function mergeLiveScorePatchIntoMatch(match, patch) {
    const merged = { ...match, ...patch };
    if (patch.liveMatch != null && match.liveMatch != null) {
        const mergedLiveMatch = { ...match.liveMatch, ...patch.liveMatch };
        const keepExistingPeriodScores =
            (!mergedLiveMatch.periodScores || mergedLiveMatch.periodScores.length === 0) &&
            match.liveMatch.periodScores?.length;
        merged.liveMatch = keepExistingPeriodScores
            ? { ...mergedLiveMatch, periodScores: match.liveMatch.periodScores }
            : mergedLiveMatch;
    }
    return merged;
}

// Returns rounds with the matching matches, updated via mergeLiveScorePatchIntoMatch.
export function updateDrawRoundMatches(rounds, matchId, patch) {
    return rounds.map((round) => ({
        ...round,
        matches: round.matches
            ? round.matches.map((match) => (match.id === matchId ? mergeLiveScorePatchIntoMatch(match, patch) : match))
            : round.matches,
    }));
}

// Returns order-of-play matches with the matching item's matchDetails updated via mergeLiveScorePatchIntoMatch.
export function updateOrderOfPlayMatches(matches, matchId, patch) {
    return matches.map((item) => {
        const itemMatchId = item.matchId || item.matchDetails?.id;
        if (itemMatchId !== matchId) {
            return item;
        }
        const matchDetails = item.matchDetails
            ? mergeLiveScorePatchIntoMatch(item.matchDetails, patch)
            : item.matchDetails;
        return { ...item, matchDetails };
    });
}

// Returns updated live match IDs and isLive flag for the event live banner.
// The banner shows only actively-live matches, so a paused (suspended or
// interrupted) or terminal match is removed via shouldRemove (DE-2765).
export function updateLiveBanner(currentIds, matchId, shouldRemove) {
    const ids = currentIds || [];
    let nextIds;
    if (shouldRemove) {
        nextIds = ids.filter((id) => id !== matchId);
    } else if (ids.includes(matchId)) {
        nextIds = ids;
    } else {
        nextIds = [...ids, matchId];
    }
    return {
        liveMatchIdsInProgress: nextIds,
        isLive: nextIds.length > 0,
    };
}

export const getRatingProgressDisplay = (progress) => {
    let reliabilityDisplay; // rounded to nearest 10%
    const ratingProgress = parseInt(progress) ?? 0;
    if (ratingProgress === 100) {
        reliabilityDisplay = 100;
    } else if (ratingProgress > 0 && ratingProgress < 15) {
        reliabilityDisplay = 10;
    } else if (ratingProgress >= 90 && ratingProgress < 100) {
        reliabilityDisplay = 90;
    } else {
        reliabilityDisplay = Math.round(ratingProgress / 10, 0) * 10;
    }
    return reliabilityDisplay;
};

export const shallowEqual = (object1, object2) => {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);
    let isEqual = true;
    if (keys1.length !== keys2.length) {
        isEqual = false;
    } else {
        keys1.forEach(key => {
            if (object1[key] !== object2[key]) {
                console.log('not equal key', key);
                isEqual = false;
            }
        });
    }
    return isEqual;
};

const getRatingColumns = ({ tennis, showTennisRatings, pickleball, showPickleBallRatings, isPtsEvent, isWTARankType }) => {
    if (isWTARankType) {
        return ['1fr', '1fr', '1fr', '1fr'];
    }
    const cols = [];
    if (tennis || showTennisRatings) {
        cols.push('1fr', '1fr');
    }
    if (pickleball || showPickleBallRatings) {
        cols.push('1fr', '1fr');
    }
    if (isPtsEvent) {
        cols.push('1fr');
    }
    return cols;
};

export const getPlayerListViewColumnGrid = (params = {}) => {
    const {
        hideRatings = false,
        pickleball,
        tennis,
        showPickleBallRatings,
        showTennisRatings,
        isPtsEvent,
        isWTARankType,
        showAdditionalInformation,
        clubProfileViewType,
        addMemberLink,
        showClubCollegeDetails,
        descriptionShort = '',
        showPlayerPaymentStatus,
        isCollegeTierType,
        displayYear,
        showRegStatus,
        eventProfileV3,
        showOrganizerViewForPTTEvent,
    } = params;

    const gridLayout = [];

    if (!hideRatings) {
        gridLayout.push(...getRatingColumns({ tennis, showTennisRatings, pickleball, showPickleBallRatings, isPtsEvent, isWTARankType }));
    }
    if (displayYear) {
        gridLayout.push('1fr');
    }
    if (isCollegeTierType) {
        gridLayout.push('1fr');
    }
    if (showAdditionalInformation) {
        gridLayout.push('1fr');
    }
    if (!showRegStatus && clubProfileViewType) {
        gridLayout.push('1fr');
        if (addMemberLink) {
            gridLayout.push('1fr');
        }
    }
    if (!showRegStatus && showClubCollegeDetails && !showAdditionalInformation && !clubProfileViewType && descriptionShort?.length > 0) {
        gridLayout.push('3fr');
    }
    if (showRegStatus) {
        gridLayout.push('1fr');
    }
    if (showPlayerPaymentStatus) {
        gridLayout.push('1fr');
    }
    if (clubProfileViewType === CLUB_PROFILE_VIEW_TYPES.COLLEGE) {
        gridLayout.push('1fr');
    }
    if (eventProfileV3) {
        gridLayout.push('1fr', '1fr', '1fr', '1fr');
    }
    if (showOrganizerViewForPTTEvent) {
        gridLayout.push('1fr');
    }

    return gridLayout;
};

export const addTrailingSpace = (text = '') => {
    return text ? `${text} ` : '';
};

export const getPlayerRegStatus = (regStatusTypeId) => {
    if (regStatusTypeId === registrationStatusTypeIds.REGISTERED) {
        return {
            label: 'Registered',
            color: 'green',
        };
    }
    if (regStatusTypeId === registrationStatusTypeIds.CREATED) {
        return {
            label: 'Pending',
            color: 'red',
        };
    }
    if (regStatusTypeId === registrationStatusTypeIds.WITHDRAWN) {
        return {
            label: 'Withdrawn',
            color: 'orange',
        };
    }
    return {
        label: '',
        color: '',
    };
};

export const getPlayerRegStatusV3 = (div) => {
    if (div) {
        if (div.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER && div.registrationStatusTypeId !== registrationStatusTypeIds.WITHDRAWN) {
            return {
                label: 'Need partner',
                title: 'Need partner',
                color: 'orange',
            };
        }
        if (div.registrationStatusTypeId === registrationStatusTypeIds.REGISTERED) {
            return {
                label: 'Registered',
                title: 'Registered',
                color: 'green',
            };
        }
        if (div.registrationStatusTypeId === registrationStatusTypeIds.CREATED) {
            return {
                label: 'Pending',
                title: 'Partner not yet registered',
                color: 'red',
            };
        }
        if (div.registrationStatusTypeId === registrationStatusTypeIds.WITHDRAWN) {
            return {
                label: 'Withdrawn',
                title: 'Withdrawn',
                color: '',
            };
        }
    }
    return {
        label: '',
        color: '',
    };
};

export const getPlayerRegDateV3 = (div, timezone, isTeamRegDateEnabled = false) => {
    if (isTeamRegDateEnabled &&
        div?.registrationGroupStatus === registrationGroupStatusTypeIds.PENDING &&
        div?.registrationStatusTypeId !== registrationStatusTypeIds.REGISTERED) {
        return null;
    }

    const dateFormat = 'MMM DD, hh:mmA';
    return div?.registeredDateTime && moment.utc(div.registeredDateTime).tz(timezone).format(dateFormat);
};

export const getTeamRegDateV3 = (div, timezone) => {
    if (div?.registrationGroupStatus === registrationGroupStatusTypeIds.PENDING) {
        return 'Awaiting partner reg.';
    }

    const dateFormat = 'MMM DD, hh:mm a';
    return div?.teamRegisteredDateTime && moment.utc(div.teamRegisteredDateTime).tz(timezone).format(dateFormat);
};

export const getDrawStartTimeV3 = (draw, timezone) => {
    const dateFormat = 'MMM DD, hh:mm a';
    return draw?.startDateTimeUtc ? moment.utc(draw.startDateTimeUtc).tz(timezone).format(dateFormat) : '-';
};

export const getPlayerPaymentStatus = (paymentStatusTypeId) => {
    if (paymentStatusTypeId === paymentStatusTypeIds.PAID) {
        return {
            label: 'Paid',
            color: 'green',
        };
    }
    if (paymentStatusTypeId === paymentStatusTypeIds.FREE) {
        return {
            label: 'Free',
            color: '',
        };
    }
    if (paymentStatusTypeId === paymentStatusTypeIds.PENDING) {
        return {
            label: 'Pending',
            color: 'orange',
        };
    }
    if (paymentStatusTypeId === paymentStatusTypeIds.WAITING) {
        return {
            label: 'Waiting',
            color: 'orange',
        };
    }
    if (paymentStatusTypeId === paymentStatusTypeIds.REFUNDED) {
        return {
            label: 'Refunded',
            color: '',
        };
    }
    if (paymentStatusTypeId === paymentStatusTypeIds.FAILED) {
        return {
            label: 'Failed',
            color: 'red',
        };
    }
    if (paymentStatusTypeId === paymentStatusTypeIds.REFUNDING) {
        return {
            label: 'Refund in Progress',
            color: 'orange',
        };
    }

    return {
        label: '',
        color: '',
    };
};

export const getPlayerPosition = (div) => {
    if (div) {
        return {
            bucket: div.isAcceptance ? acceptanceBucketTypeIds[div.type] : waitlistBucketTypeIds[div.type],
            position: div.position,
            order: div.order,
        };
    }
    return null;
};

export const isValidPlayerName = (name = '') => {
    const playerNameSections = name?.trim()?.split(' ');
    return playerNameSections?.length > 1;
};

// Lodash methods reimplemented - https://youmightnotneed.com/lodash
export const sumBy = (arr, func) => arr.reduce((acc, item) => acc + func(item), 0);

export const getTiebreak = (scoreItem) => {
    const tiebreak = wonSet(scoreItem, playerTypes.WINNER) ? scoreItem.tiebreak : scoreItem.winnerTiebreak;
    return tiebreak ? `(${tiebreak})` : '';
};

export const sortType = {
    ASC: 'asc',
    DESC: 'desc',
};

export const getComparatorArguments = (srtType) => {
    const args = [];
    const primarySortArg = {
        type: ComparatorTypes.NUMBER,
        field: 'order',
        descend: srtType === sortType.DESC,
    };
    args.push(primarySortArg);
    const secondarySortArg = {
        type: ComparatorTypes.NUMBER,
        field: 'position',
        descend: srtType === sortType.DESC,
    };
    args.push(secondarySortArg);
    return args;
};
